import { AnalyticsContext } from '@shared/contexts/AnalyticsContext';
import { useEmailThreadContext } from '@shared/contexts/pluginContexts/EmailThreadContext';
import { ResponseDraftContext } from '@shared/contexts/ResponseDraftContext';
import { VoomaApiClient } from '@shared/generated/graphql';
import { StyleProps } from '@shared/utils/shared-types';
import clsx from 'clsx';
import { isNil } from 'lodash';
import { useContext } from 'react';
import { LuMail, LuReplyAll } from 'react-icons/lu';
import { Button } from '../button';
import { CircularProgress } from '../circular-progress';
import { CopyButton } from '../copy-button';

type MessageActionButtonProps = StyleProps & {
  compact?: boolean;
  messageType?: string;
  draftReply?: (fallbackItemId?: string) => void;
  draftReplyInProgress?: boolean;
  composeMessage?: () => void;
  composeMessageInProgress?: boolean;
  draft?: string;
};

export function MessageActionButton({
  className,
  compact,
  draftReply,
  draftReplyInProgress,
  composeMessage,
  composeMessageInProgress,
  draft,
  messageType = 'email',
}: MessageActionButtonProps) {
  const { hideInsertDraftButton } = useEmailThreadContext();
  const { itemId } = useContext(ResponseDraftContext);
  const { apiClient } = useContext(AnalyticsContext);

  if (isNil(draftReply) && isNil(composeMessage)) {
    return null;
  }

  const removeInsertDraftButton = [
    hideInsertDraftButton || VoomaApiClient.CHROME,
  ].includes(apiClient);
  const primaryCTAClassName = clsx(
    compact ? 'flex-grow max-w-full mx-2' : 'flex-grow max-w-full mx-4',
    removeInsertDraftButton && 'mx-[4px] flex-grow-0'
  );
  const containerClassname = clsx(
    className,
    compact ? 'flex flex-row items-center justify-center' : 'flex flex-row pb-4'
  );

  if (draft && removeInsertDraftButton) {
    return (
      <div className={containerClassname}>
        <CopyButton
          className="w-full flex-grow"
          size="medium"
          variant="primary"
          textToCopy={draft}
          label="Copy draft"
        />
      </div>
    );
  } else if (!draft && removeInsertDraftButton) {
    return null;
  }

  return (
    <div className={containerClassname}>
      {isNil(draftReply) ? (
        <Button
          variant="primary"
          className={primaryCTAClassName}
          onPress={composeMessage}
          isDisabled={composeMessageInProgress}
          icon={<LuMail className="text-white" size={24} />}
          tooltip={`Compose ${messageType}`}
        >
          Compose email
        </Button>
      ) : (
        <Button
          variant="primary"
          className={primaryCTAClassName}
          onPress={() => draftReply(itemId)}
          isDisabled={draftReplyInProgress}
          tooltip={`This will open as a new ${messageType} draft.`}
          icon={
            draftReplyInProgress ? (
              <CircularProgress className="text-white" size="xs" />
            ) : (
              <LuReplyAll
                className="scale-x-[-1] transform text-white"
                size={24}
              />
            )
          }
        >
          {'Insert draft response'}
        </Button>
      )}
    </div>
  );
}

export default MessageActionButton;
